import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, useAuth } from '@archicore/shared';
import { Loader2, Eye, EyeOff, ShieldCheck, Terminal, Fingerprint, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, initializing } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const ADMIN_EMAILS = ['contacto@archicoreanalytic.com', 'arqbonnel@gmail.com', 'master@archicore.app'];

    useEffect(() => {
        if (!initializing && user) {
            const normalizedEmail = (user.email || '').trim().toLowerCase();
            if (ADMIN_EMAILS.includes(normalizedEmail)) {
                window.location.href = '/';
            }
        }
    }, [user, initializing]);

    const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        console.log("[AuthFlow] Intentando ingreso con:", email.trim().toLowerCase());
        
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
            const loggedUser = userCredential.user;
            console.log("[AuthFlow] Usuario autenticado por Firebase:", loggedUser.email);

            const normalizedEmail = (loggedUser.email || '').trim().toLowerCase();
            
            if (!ADMIN_EMAILS.includes(normalizedEmail)) {
                console.warn("[AuthFlow] Acceso denegado: Email no está en la lista blanca.");
                await auth.signOut();
                localStorage.removeItem('archicore_cached_session');
                setError('ACCESO RESTRINGIDO: No posees privilegios de administrador de sistemas.');
                setLoading(false);
                return;
            }

            // Persistir caché de sesión para entrada instantánea
            localStorage.setItem('archicore_cached_session', JSON.stringify({ uid: loggedUser.uid, email: loggedUser.email }));
            console.log("[AuthFlow] Acceso autorizado. Redirigiendo al Dashboard...");
            window.location.href = '/';
        } catch (err: any) {
            console.error("[AuthFlow] Error en login:", err);
            setError('Identidad no verificada. Revisa tus credenciales.');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-50">
            {/* Background Decorative Elements */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 blur-[120px] rounded-full" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 blur-[120px] rounded-full" />
            
            <div className="max-w-md w-full relative z-10 mx-4">
                <div className="glass-card-accent p-10 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] overflow-hidden relative border border-slate-200 bg-white">
                    {/* Top Accent Bar */}
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-cyan-600 via-orange-500 to-indigo-600 shadow-sm" />

                    <div className="flex flex-col items-center mb-10">
                        <div className="w-24 h-24 bg-slate-900 rounded-3xl flex items-center justify-center shadow-xl mb-6 p-4">
                            <ShieldCheck size={48} className="text-white" />
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
                            ArchiCore <span className="text-orange-500">Analytic</span>
                        </h1>
                        <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 rounded-full">
                            <Terminal size={12} className="text-slate-500" />
                            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Consola de Administración</span>
                        </div>
                    </div>

                    <form onSubmit={handleSignIn} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">E-mail Corporativo</label>
                            <div className="relative">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="neo-input h-14 border-slate-200"
                                    placeholder="leo@archicore.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-400 ml-1">Clave de Acceso</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="neo-input h-14 pr-12 border-slate-200"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-100 text-red-600 text-sm p-4 rounded-xl flex items-start gap-3 animate-shake">
                                <Activity size={18} className="shrink-0 mt-0.5" />
                                <span className="font-medium">{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full h-14 bg-slate-900 hover:bg-slate-800 flex items-center justify-center gap-3 shadow-lg shadow-slate-200"
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" size={24} />
                            ) : (
                                <>
                                    <Fingerprint size={24} />
                                    <span className="uppercase tracking-widest font-bold">VERIFICAR IDENTIDAD</span>
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-10 pt-8 border-t border-slate-100 text-center">
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                            ArchiCore Analytic HQ © 2026
                        </p>
                        <p className="text-[10px] text-slate-300 mt-1 uppercase tracking-widest">
                            Nivel de Seguridad: <span className="text-slate-400 font-bold">Máximo</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
